import { __spreadArrays } from "tslib";
import * as React from 'react';
import { Text, DeprecatedSelect, Column } from '@wix/answers-ui-libby';
import { compKey } from './key';
import { CustomFieldType } from '@wix/answers-api';
import { ErrorMsg } from '../contact-form/contact-form-utils';
import { classNames } from '@wix/answers-lib';
export var MULTILEVEL_DELIMITER = '|';
var isLastLevel = function (levelValue) { var _a; return !((_a = levelValue.children) === null || _a === void 0 ? void 0 : _a.length) || levelValue.children.every(function (c) { return c.disabled; }); };
var optionRenderer = function (option, optionState) {
    var isSelected = optionState === null || optionState === void 0 ? void 0 : optionState.isSelected;
    return (React.createElement(Text, { color: isSelected ? 't01' : 't30', type: 'hs', className: classNames('multi-level-field-option') }, option.label));
};
var addClearOption = function (optionsWithoutClear, mandatory, clearText) {
    if (mandatory) {
        return optionsWithoutClear;
    }
    var clearOption = {
        value: '',
        label: clearText,
    };
    return __spreadArrays([clearOption], optionsWithoutClear);
};
var getAllOptions = function (children) {
    var options = children
        .filter(function (levelValue) { return !levelValue.disabled; })
        .map(function (levelValue) { return ({ value: levelValue.value, label: levelValue.value }); });
    return options;
};
var getSelectedLevels = function (selectedValues, levelValues, currentLevelNumber) {
    if (currentLevelNumber === void 0) { currentLevelNumber = 0; }
    var currentLevelSelectedValue = selectedValues[currentLevelNumber];
    if (!currentLevelSelectedValue) {
        return [];
    }
    var nextLevel = levelValues.find(function (levelValue) { return levelValue.value === currentLevelSelectedValue; });
    if (!nextLevel) {
        return [];
    }
    return __spreadArrays([nextLevel], getSelectedLevels(selectedValues, nextLevel.children || [], currentLevelNumber + 1));
};
var getTranslatedValue = function (value, locale) {
    return (locale && value.translatedValues && value.translatedValues[locale]) ?
        value.translatedValues[locale].value : '';
};
var getTranslatedSelectedOption = function (levelValues, selected, locale) {
    var selectedMaybeTranslated = levelValues.filter(function (value) { return value.value === selected; });
    return selectedMaybeTranslated.length ?
        { value: selected, label: getTranslatedValue(selectedMaybeTranslated[0], locale) } : undefined;
};
var isTranslatedDisabled = function (value, locale) {
    var returned = (locale && value.translatedValues && value.translatedValues[locale]) ?
        !value.translatedValues[locale].value : true;
    return returned;
};
var getTranslatedOptions = function (levelValues, locale) {
    return levelValues.filter(function (levelValue) { return !isTranslatedDisabled(levelValue, locale); })
        .map(function (levelValue) {
        var translatedVal = getTranslatedValue(levelValue, locale);
        return { value: levelValue.value, label: translatedVal };
    });
};
export var PublicMultilevelField = function (props) {
    var field = props.field, value = props.value, onChange = props.onChange;
    if (field.type !== CustomFieldType.MULTI_LEVEL || !field.multiLevelData) {
        return null;
    }
    var isPrimaryLocaleSelected = props.locale === props.primaryLocale;
    var selectedValues = value ? value.split(MULTILEVEL_DELIMITER) : [];
    var selectedLevels = getSelectedLevels(selectedValues, field.multiLevelData.values);
    var renderLevel = function (children, levelNumber) {
        var _a, _b, _c;
        if (levelNumber === void 0) { levelNumber = 0; }
        var selectedValue = selectedValues[levelNumber];
        var selectedValueOption = selectedValue ? {
            value: selectedValue,
            label: selectedValue,
        } : undefined;
        var isPrimaryIsInEnabledLocales = (_a = props.field.multiLevelData) === null || _a === void 0 ? void 0 : _a.enabledLocales.includes(props.locale);
        var maybeTranslatedSelectedValueOption = (!isPrimaryLocaleSelected || isPrimaryIsInEnabledLocales) ?
            getTranslatedSelectedOption(children, selectedValue, props.locale) : selectedValueOption;
        var getTranslatedTitle = function () {
            var _a;
            if (field.multiLevelData && props.locale) {
                return ((_a = field.multiLevelData.fieldLevels[levelNumber]) === null || _a === void 0 ? void 0 : _a.translatedTitles) ? field.multiLevelData.fieldLevels[levelNumber].translatedTitles[props.locale] : undefined;
            }
            return undefined;
        };
        var levelTitle = props.locale && getTranslatedTitle() || ((_c = (_b = field.multiLevelData) === null || _b === void 0 ? void 0 : _b.fieldLevels[levelNumber]) === null || _c === void 0 ? void 0 : _c.title);
        var onSelectChange = function (selectedOption) {
            if (!selectedOption.value) {
                onChange({ value: selectedValues.slice(0, levelNumber).join(MULTILEVEL_DELIMITER), isComplete: false });
                return;
            }
            var newValue = __spreadArrays(selectedValues.slice(0, levelNumber), [selectedOption.value]).join(MULTILEVEL_DELIMITER);
            var selectedLevelValue = children.find(function (c) { return c.value === selectedOption.value; });
            if (!selectedLevelValue) {
                onChange({ isComplete: false, value: newValue });
                return;
            }
            onChange({ value: newValue, isComplete: isLastLevel(selectedLevelValue) });
        };
        var maybeTranslatedOptions = (!isPrimaryLocaleSelected || isPrimaryIsInEnabledLocales) ?
            getTranslatedOptions(children, props.locale) :
            getAllOptions(children);
        var maybeClearOptionAddedToOptions = addClearOption(maybeTranslatedOptions, props.mandatory, props.texts.clearText);
        if (!maybeClearOptionAddedToOptions.filter(function (option) { return option.value; }).length) {
            return null;
        }
        var isEmptyValue = !selectedValue;
        return (React.createElement("div", { className: compKey + "-level-" + levelNumber },
            React.createElement(Column, { gap: '8' },
                React.createElement(Text, { className: compKey + "-level-title", relativeToBody: true, color: 't30' }, levelTitle),
                React.createElement(DeprecatedSelect, { value: maybeTranslatedSelectedValueOption, options: maybeClearOptionAddedToOptions, onChange: onSelectChange, placeholder: props.texts.placeholder, className: classNames({ 'empty-level': isEmptyValue }), searchable: false, optionRenderer: optionRenderer, autoFocus: isEmptyValue && !!levelNumber }),
                isEmptyValue && props.validationError && props.mandatory ?
                    React.createElement(ErrorMsg, { isVisible: true, msg: props.validationError }) : null)));
    };
    return (React.createElement("div", { className: compKey + "-wrapper" },
        React.createElement(Column, { className: compKey, gap: '14' },
            renderLevel(field.multiLevelData.values),
            React.createElement(Column, { gap: '14', className: compKey + "-levels" }, selectedLevels.map(function (selectedLevel, idx) { return renderLevel(selectedLevel.children || [], idx + 1); })))));
};
//# sourceMappingURL=index.js.map